import "./styles/main.css";
import { GameSession } from "./chess/game-session.js";
import { MatchController } from "./chess/match-controller.js";
import { AppState, StateMachine } from "./app/state-machine.js";
import { AssetResolver } from "./assets/asset-resolver.js";
import { SaveStore } from "./persistence/save-store.js";
import { EngineController } from "./engine/engine-controller.js";
import { uciToMove } from "./engine/uci.js";
import { PERFORMANCE_ANALYSIS } from "./engine/opponent-policy.js";
import {
  OpponentPerformanceDirector,
  classifyMoveQuality,
  moodForHumanEvaluation,
  normalizeEngineScore,
  phaseFor,
  tacticalMotif,
} from "./commentary/opponent-performance-director.js";
import { AudioManager } from "./audio/audio-manager.js";
import {
  confettiMarkup,
  outcomePresentation,
} from "./presentation/outcome-presentation.js";
import { BoardHost } from "./presentation/board-host.js";
import { createBoardViewState } from "./presentation/board-view-state.js";
import {
  cycleBoardSquare,
  gamepadDirection,
  moveBoardSquare,
  orderedSquares,
} from "./input/board-controls.js";

const PIECES = {
  wk: "♔︎",
  wq: "♕︎",
  wr: "♖︎",
  wb: "♗︎",
  wn: "♘︎",
  wp: "♙︎",
  bk: "♚︎",
  bq: "♛︎",
  br: "♜︎",
  bb: "♝︎",
  bn: "♞︎",
  bp: "♟︎",
};
const PIECE_NAMES = {
  k: "king",
  q: "queen",
  r: "rook",
  b: "bishop",
  n: "knight",
  p: "pawn",
};
const DEFAULT_SETTINGS = {
  coordinates: true,
  legalHighlights: true,
  reducedMotion: false,
  resignConfirm: true,
  music: true,
  sfx: true,
  voice: true,
  musicVolume: 0.35,
  sfxVolume: 0.7,
  voiceVolume: 0.7,
  boardViewMode: "2d",
  threePieceSetId: "low_poly_classic",
};

class ChessApp {
  constructor(root) {
    this.root = root;
    this.machine = new StateMachine();
    this.store = new SaveStore();
    this.session = new GameSession();
    this.match = null;
    this.boardAnimating = false;
    this.releaseId = "current";
    this.engine = new EngineController();
    this.selected = null;
    this.legal = [];
    this.focusedSquare = "e2";
    this.gamepadIndex = null;
    this.gamepadButtons = [];
    this.gamepadHeldDirection = null;
    this.gamepadRepeatAt = 0;
    this.gamepadFrame = null;
    this.dragFrom = null;
    this.dragPointerId = null;
    this.dragStart = null;
    this.dragGhost = null;
    this.dragMoved = false;
    this.suppressSquareClick = false;
    this.scrollToTopNext = false;
    this.pendingPromotion = null;
    this.forcedResult = null;
    this.modal = null;
    this.commentary = "Welcome to the lodge.";
    this.positionAnalysis = null;
    this.hint = null;
    this.hintLoading = false;
    this.lastMoveWasEngine = false;
    this.lastHumanMoveContext = null;
    this.lastPhase = "opening";
    this.idleTimer = null;
    /** @type {any} */ this.game = null;
    /** @type {any[]} */ this.opponents = [];
    /** @type {any[]} */ this.difficulties = [];
    /** @type {any[]} */ this.themes = [];
    /** @type {any} */ this.dialogue = {};
    /** @type {any[]} */ this.openingBooks = [];
    /** @type {any[]} */ this.pieceSets = [];
    /** @type {any} */ this.theme = null;
    /** @type {any} */ this.saved = null;
    /** @type {any} */ this.resolver = null;
    /** @type {any} */ this.audio = null;
    /** @type {any} */ this.commentaryProvider = null;
    this.config = {
      mode: "computer",
      opponentId: "the_jock",
      difficultyId: "casual",
      humanSide: "w",
      orientation: "w",
    };
    this.settings = { ...DEFAULT_SETTINGS };
    this.boardHost = new BoardHost({
      onSquareActivate: (square) => void this.chooseSquare(square),
      onMoveIntent: (move) => void this.moveFromBoard(move),
      getLegalMoves: (square) => this.session.legalMoves(square),
      onRendererFailure: (message) => void this.fallbackTo2d(message),
      onPieceSetFallback: (message) => this.pieceSetFallback(message),
    });
    this.engine.addEventListener("error", (event) =>
      this.engineFailure(event.detail),
    );
  }

  async initialize() {
    try {
      const [
        game,
        opponents,
        difficulties,
        themes,
        dialogue,
        openingBooks,
        pieceSets,
        manifest,
        settings,
        saved,
      ] = await Promise.all([
        loadJson("game"),
        loadJson("opponents"),
        loadJson("difficulties"),
        loadJson("themes"),
        loadJson("dialogue"),
        loadJson("opening_books"),
        loadJson("piece_sets"),
        loadJson("asset-manifest"),
        this.store.settings(),
        this.store.loadActive(),
      ]);
      Object.assign(this, {
        game,
        opponents,
        difficulties,
        themes,
        dialogue,
        openingBooks,
        pieceSets,
        saved,
        releaseId: import.meta.env.VITE_CHESS_BUILD_ID ?? manifest.buildId,
      });
      this.settings = { ...DEFAULT_SETTINGS, ...settings };
      if (!["2d", "3d"].includes(this.settings.boardViewMode)) {
        this.settings.boardViewMode = "2d";
      }
      if (
        !pieceSets.some(
          (item) =>
            item.id === this.settings.threePieceSetId &&
            item.dimension === "3d",
        )
      ) {
        this.settings.threePieceSetId = game.default3dPieceSetId;
      }
      this.resolver = new AssetResolver(manifest);
      this.audio = new AudioManager(this.resolver);
      this.audio.configure(this.settings);
      this.commentaryProvider = new OpponentPerformanceDirector(dialogue);
      this.applyTheme(
        themes.find((theme) => theme.id === game.defaultThemeId) ?? themes[0],
      );
      this.config.opponentId = game.defaultOpponentId;
      this.config.difficultyId = game.defaultDifficultyId;
      this.bindGlobalEvents();
      this.render();
      if (this.settings.boardViewMode === "3d") {
        await this.changeBoardMode("3d", { announce: false });
      }
      this.registerServiceWorker();
    } catch (error) {
      this.root.innerHTML = `<main class="fatal"><h1>The lodge could not open</h1><p>${escapeHtml(error.message)}</p><button data-action="reload">Try again</button></main>`;
      this.root
        .querySelector("button")
        ?.addEventListener("click", () => location.reload());
    }
  }

  bindGlobalEvents() {
    this.root.addEventListener("click", (event) => this.onClick(event));
    this.root.addEventListener("change", (event) => this.onChange(event));
    this.root.addEventListener("keydown", (event) => this.onKeyDown(event));
    window.addEventListener("gamepadconnected", (event) =>
      this.connectGamepad(event.gamepad),
    );
    window.addEventListener("gamepaddisconnected", (event) =>
      this.disconnectGamepad(event.gamepad),
    );
    const connected = [...(navigator.getGamepads?.() ?? [])].find(Boolean);
    if (connected) {
      this.connectGamepad(connected);
    }
  }

  render() {
    const isHome = this.machine.value === AppState.HOME;
    this.boardHost.detach();
    this.root.innerHTML = `${isHome ? this.homeTemplate() : this.gameTemplate()}${this.dialogTemplate()}<div id="live-region" class="sr-only" aria-live="polite">${escapeHtml(this.liveMessage ?? "")}</div>`;
    this.root.dataset.controller = this.gamepadIndex === null ? "off" : "on";
    document.body.classList.toggle(
      "reduced-motion",
      this.settings.reducedMotion,
    );
    if (!isHome) {
      const container = this.root.querySelector("[data-board-host]");
      if (container) {
        this.boardHost.attach(container);
        this.boardHost.update(this.boardViewState());
      }
    }
    if (!isHome && this.focusedSquare) {
      this.boardHost.focusSquare(this.focusedSquare);
    }
    if (this.modal) {
      queueMicrotask(() => this.openDialog(this.modal));
    }
    if (this.scrollToTopNext) {
      this.scrollToTopNext = false;
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  }

  homeTemplate() {
    const opponent = this.currentOpponent();
    const intro = this.resolver.ui("intro_splash_page");
    const menu = this.resolver.ui("menu_background");
    const logo = this.resolver.ui("logo");
    const portrait = this.resolver.portrait(opponent);
    const background = intro ?? menu;
    const backgroundUrl = cssAssetUrl(background);
    return `<main id="main" class="home-shell" ${backgroundUrl ? `style="--home-image:url(&quot;${escapeHtml(backgroundUrl)}&quot;)"` : ""}>
      <section class="home-hero">
        <div class="brand-mark">${logo ? `<img src="${logo.url}" alt="BrawnyAi">` : `<span class="brand-wordmark">BrawnyAi</span>`}<h1>Chess</h1></div>
        <p class="tagline">${escapeHtml(this.game.tagline)}</p>
        <div class="lodge-emblem" aria-hidden="true"><span>♞</span></div>
        <p class="hero-note">Classic chess, fireside company, and no connection required.</p>
      </section>
      <section class="new-game-card" aria-labelledby="new-game-title">
        <div class="card-heading"><div>${portrait ? `<img class="opponent-avatar image" src="${portrait.url}" alt="">` : avatar(opponent)}</div><div><p class="eyebrow">Your next opponent</p><h2 id="new-game-title">${escapeHtml(opponent.name)}</h2><p>${escapeHtml(opponent.descriptor)}</p></div></div>
        <div class="form-grid">
          <label>Game mode<select data-setting="mode"><option value="computer" ${selected(this.config.mode, "computer")}>Human vs computer</option><option value="local" ${selected(this.config.mode, "local")}>Local two-player</option></select></label>
          <label>Opponent<select data-setting="opponentId">${this.opponents.map((item) => `<option value="${item.id}" ${selected(this.config.opponentId, item.id)}>${escapeHtml(item.name)}</option>`).join("")}</select></label>
          <label>Play as<select data-setting="side" ${this.config.mode === "local" ? "disabled" : ""}><option value="w" ${selected(this.config.humanSide, "w")}>White</option><option value="b" ${selected(this.config.humanSide, "b")}>Black</option><option value="random">Random</option></select></label>
          <label>Difficulty<select data-setting="difficultyId" ${this.config.mode === "local" ? "disabled" : ""}>${this.difficulties.map((item) => `<option value="${item.id}" ${selected(this.config.difficultyId, item.id)}>${escapeHtml(item.name)} — ${escapeHtml(item.description)}</option>`).join("")}</select></label>
          <label>Board orientation<select data-setting="orientation"><option value="auto">Follow player</option><option value="w" ${selected(this.config.orientation, "w")}>White at bottom</option><option value="b" ${selected(this.config.orientation, "b")}>Black at bottom</option></select></label>
        </div>
        <div class="home-actions"><button class="button primary" data-action="start">Start new game</button>${this.saved ? `<button class="button secondary" data-action="resume">Resume game</button>` : ""}</div>
        <div class="text-actions"><button data-action="import">Import PGN</button><button data-action="settings">Settings</button><button data-action="about">About & licenses</button></div>
      </section>
      <footer>Entirely local · No tracking · Installable</footer>
    </main>`;
  }

  gameTemplate() {
    const view = this.session.view();
    const opponent = this.currentOpponent();
    const portrait = this.resolver.portrait(opponent);
    const video = this.resolver.video(opponent);
    const orientation = this.config.orientation;
    const hintAvailable =
      this.config.mode === "computer" &&
      this.machine.value === AppState.HUMAN_TURN &&
      view.turn === this.config.humanSide;
    const hintLabel = this.hintLoading
      ? "Finding a move…"
      : this.hint?.stage === 1
        ? "Show piece"
        : this.hint?.stage === 2
          ? "Show move"
          : this.hint?.stage === 3
            ? "Move shown"
            : "Hint";
    const boardBackground = this.resolver.board(
      this.game.defaultThemeId,
      "background",
    );
    const boardBackgroundUrl = cssAssetUrl(boardBackground);
    return `<main id="main" class="game-shell" ${boardBackgroundUrl ? `style="--game-background:url(&quot;${escapeHtml(boardBackgroundUrl)}&quot;)"` : ""}>
      <header class="game-header"><button class="icon-button" data-action="home" aria-label="Back to home">←</button><div class="game-heading"><div class="game-title-line"><strong>Chess</strong><span>by BrawnyAi</span></div><span class="match-context">${this.config.mode === "local" ? "Local match" : `Playing ${escapeHtml(opponent.name)}`}</span></div><div class="board-view-toolbar" role="group" aria-label="Board view"><span>Board view</span><button data-view-mode="2d" aria-pressed="${this.settings.boardViewMode === "2d"}">2D</button><button data-view-mode="3d" aria-pressed="${this.settings.boardViewMode === "3d"}">3D</button></div><button class="icon-button" data-action="settings" aria-label="Settings">⚙</button></header>
      <section class="opponent-card">
        <div class="media-frame">${video ? `<video src="${video.url}" ${portrait ? `poster="${portrait.url}"` : ""} muted loop playsinline autoplay data-fallback="video"></video>` : portrait ? `<img src="${portrait.url}" alt="${escapeHtml(opponent.name)}">` : avatar(opponent)}</div>
        <div class="opponent-copy"><h1>${this.config.mode === "local" ? "Across the board" : escapeHtml(opponent.name)}</h1><p class="commentary">“${escapeHtml(this.commentary)}”</p></div>
      </section>
      <section class="board-column">
        <div class="player-strip top">${this.playerStrip(orientation === "w" ? "b" : "w", view)}</div>
        <div class="board-stage">
          <div class="board-wrap">
            <div class="board-host" data-board-host></div>
          </div>
        </div>
        <div class="player-strip bottom">${this.playerStrip(orientation, view)}</div>
        <div class="game-actions" role="group" aria-label="Game actions"><button class="hint-button" data-action="hint" ${hintAvailable && !this.hintLoading && this.hint?.stage !== 3 ? "" : "disabled"}>💡 ${hintLabel}</button><button data-action="undo" ${view.history.length ? "" : "disabled"}>↶ Undo</button><button data-action="flip">⇅ Flip</button><button data-action="more" aria-haspopup="dialog">More ⋯</button></div>
      </section>
      <aside class="game-sidebar">
        <div class="game-summary"><section class="captured-panel" aria-label="Captured pieces"><h2>Captured</h2><div>${capturedPieces(view.history)}</div></section><button class="history-button" data-action="history" aria-haspopup="dialog">Move trail · ${Math.ceil(view.history.length / 2)}</button></div>
        <section class="move-panel"><div class="panel-title"><h2>Move trail</h2><span>${Math.ceil(view.history.length / 2)} turns</span></div><ol class="move-list">${historyRows(view.history)}</ol></section>
      </aside>
    </main>`;
  }

  squareTemplate(square, view, last) {
    const piece = view.board.find((item) => item?.square === square);
    const legal = this.legal.find((move) => move.to === square);
    const isSelected = this.selected === square;
    const isHintSource =
      this.hint?.stage >= 2 && this.hint.move.from === square;
    const isHintTarget = this.hint?.stage >= 3 && this.hint.move.to === square;
    const isLast = last && (last.from === square || last.to === square);
    const isEngineArrival = this.lastMoveWasEngine && last?.to === square;
    const check =
      view.status.check && piece?.type === "k" && piece.color === view.turn;
    const classes = [
      squareColor(square),
      legal && this.settings.legalHighlights ? "legal" : "",
      legal?.captured ? "capture" : "",
      isSelected ? "selected" : "",
      isLast ? "last-move" : "",
      isEngineArrival ? "engine-arrival" : "",
      check ? "in-check" : "",
      isHintSource ? "hint-source" : "",
      isHintTarget ? "hint-target" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const file = square[0],
      rank = square[1];
    const showFile =
      this.settings.coordinates &&
      ((this.config.orientation === "w" && rank === "1") ||
        (this.config.orientation === "b" && rank === "8"));
    const showRank =
      this.settings.coordinates &&
      ((this.config.orientation === "w" && file === "a") ||
        (this.config.orientation === "b" && file === "h"));
    const label = `${square}, ${piece ? `${piece.color === "w" ? "white" : "black"} ${PIECE_NAMES[piece.type]}` : "empty"}${legal ? ", legal destination" : ""}${isHintSource ? ", suggested piece" : ""}${isHintTarget ? ", suggested destination" : ""}`;
    const customPiece = piece
      ? this.resolver.piece(
          this.game.defaultPieceSetId,
          piece.color,
          piece.type,
        )
      : null;
    return `<button class="square ${classes}" role="gridcell" data-square="${square}" aria-label="${label}" aria-selected="${isSelected}" tabindex="${square === this.focusedSquare ? "0" : "-1"}">${piece ? (customPiece ? `<img class="piece custom-piece" src="${customPiece.url}" alt="" aria-hidden="true">` : `<span class="piece ${piece.color === "w" ? "white-piece" : "black-piece"}" aria-hidden="true">${PIECES[piece.color + piece.type]}</span>`) : ""}${isHintSource ? `<span class="hint-marker" aria-hidden="true">1</span>` : ""}${isHintTarget ? `<span class="hint-marker target" aria-hidden="true">2</span>` : ""}${showFile ? `<span class="coordinate file">${file}</span>` : ""}${showRank ? `<span class="coordinate rank">${rank}</span>` : ""}</button>`;
  }

  boardViewState() {
    const view = this.session.view();
    const texture = this.resolver.board(
      this.game.defaultThemeId,
      "board_texture",
    );
    const pieceSetId =
      this.settings.boardViewMode === "3d"
        ? this.settings.threePieceSetId
        : this.game.defaultPieceSetId;
    const definition =
      this.pieceSets.find((item) => item.id === pieceSetId) ??
      this.pieceSets.find((item) => item.id === "low_poly_classic");
    const model =
      definition?.source === "glb"
        ? this.resolver.pieceModel(definition.id)
        : null;
    return createBoardViewState({
      view,
      orientation: this.config.orientation,
      selected: this.selected,
      legal: this.legal,
      focusedSquare: this.focusedSquare,
      hint: this.hint,
      settings: this.settings,
      interactive:
        this.machine.value === AppState.HUMAN_TURN && !this.boardAnimating,
      playerSide: this.config.humanSide,
      localMode: this.config.mode === "local",
      lastMoveWasEngine: this.lastMoveWasEngine,
      boardTextureUrl: cssAssetUrl(texture),
      theme: this.theme?.tokens ?? {},
      pieceSetId,
      pieceSet: definition
        ? { ...definition, modelUrl: cssAssetUrl(model) }
        : null,
      resolvePiece: (color, type) =>
        this.resolver.piece(this.game.defaultPieceSetId, color, type)?.url ??
        null,
    });
  }

  async changeBoardMode(mode, options = {}) {
    if (!["2d", "3d"].includes(mode)) {
      return;
    }
    this.root.dataset.boardViewLoading = mode;
    const result = await this.boardHost.switchMode(mode);
    delete this.root.dataset.boardViewLoading;
    if (!result.ok) {
      if (!result.cancelled) {
        this.settings.boardViewMode = "2d";
        await this.store.saveSettings(this.settings);
        this.liveMessage = `The 3D board is unavailable. ${result.error?.message ?? "Using 2D instead."}`;
        if (this.boardHost.mode !== "2d") {
          await this.boardHost.switchMode("2d");
        }
        this.render();
      }
      return;
    }
    this.settings.boardViewMode = mode;
    await this.store.saveSettings(this.settings);
    if (options.announce !== false) {
      this.liveMessage = `${mode === "3d" ? "3D" : "2D"} board selected.`;
    }
    this.render();
  }

  async fallbackTo2d(message) {
    this.settings.boardViewMode = "2d";
    await this.store.saveSettings(this.settings);
    await this.boardHost.switchMode("2d");
    this.liveMessage = message;
    this.render();
  }

  pieceSetFallback(message) {
    this.settings.threePieceSetId = "low_poly_classic";
    void this.store.saveSettings(this.settings);
    this.liveMessage = `The selected 3D pieces could not load. Using Low-poly Classic. ${message}`;
    const liveRegion = this.root.querySelector("#live-region");
    if (liveRegion) {
      liveRegion.textContent = this.liveMessage;
    }
  }

  playerStrip(color, view) {
    const status =
      this.forcedResult?.text ??
      (view.status.check && !view.status.gameOver
        ? `${color === "w" ? "White" : "Black"} in check${this.machine.value === AppState.ENGINE_THINKING ? " · Thinking…" : ""}`
        : this.statusText(view));
    const isOpponent =
      this.config.mode === "computer" && color !== this.config.humanSide;
    const name =
      this.config.mode === "local"
        ? color === "w"
          ? "White player"
          : "Black player"
        : isOpponent
          ? this.currentOpponent().name
          : "You";
    return `<span class="mini-piece">${color === "w" ? "♔" : "♚"}</span><strong>${escapeHtml(name)}</strong>${view.turn === color ? `<span class="active-turn ${view.status.check ? "check" : ""}">${escapeHtml(status)}</span>` : ""}`;
  }

  dialogTemplate() {
    const promotion = this.pendingPromotion
      ? `<dialog id="promotion-dialog" aria-labelledby="promotion-title"><h2 id="promotion-title">Choose promotion</h2><p>Your pawn reached the far side. Choose its new piece.</p><div class="promotion-grid">${["q", "r", "b", "n"].map((piece) => `<button data-promote="${piece}" aria-label="Promote to ${PIECE_NAMES[piece]}">${PIECES[this.session.turn() + piece]}<span>${title(PIECE_NAMES[piece])}</span></button>`).join("")}</div><button data-action="close-dialog">Cancel</button></dialog>`
      : "";
    const view = this.session.view();
    const resultDetail = this.forcedResult?.text ?? this.statusText(view);
    const presentation = outcomePresentation({
      mode: this.config.mode,
      humanSide: this.config.humanSide,
      result: this.forcedResult?.result ?? view.status.result,
      detail: resultDetail,
      opponentName: this.currentOpponent().name,
    });
    const portrait = this.resolver?.portrait(this.currentOpponent());
    const speaker =
      this.config.mode === "computer"
        ? `<div class="result-speaker">${portrait ? `<img src="${portrait.url}" alt="">` : avatar(this.currentOpponent())}<div><strong>${escapeHtml(this.currentOpponent().name)}</strong><p>“${escapeHtml(this.commentary)}”</p></div></div>`
        : `<p class="result-neutral-copy">${escapeHtml(this.commentary)}</p>`;
    const gameOver =
      this.machine.value === AppState.GAME_OVER
        ? `<dialog id="gameover-dialog" class="gameover-dialog outcome-${presentation.kind}" aria-labelledby="gameover-title">${presentation.kind === "win" ? confettiMarkup() : ""}<div class="result-content"><p class="eyebrow">${escapeHtml(presentation.eyebrow)}</p><h2 id="gameover-title">${escapeHtml(presentation.headline)}</h2><p class="result-detail">${escapeHtml(presentation.detail)}</p>${speaker}<div class="dialog-actions"><button class="button primary" data-action="rematch">Rematch</button><button class="button secondary" data-action="new-game">New game</button></div></div></dialog>`
        : "";
    const utilityDialogs = {
      more: () =>
        this.utilityDialog(
          "more",
          "More game options",
          `<div class="more-actions"><button data-action="copy-fen">Copy FEN</button><button data-action="export">Export PGN</button><button data-action="import">Import PGN</button><button data-action="controls">Controls help</button><button class="danger-ghost" data-action="resign">Resign</button></div>`,
        ),
      controls: () =>
        this.utilityDialog(
          "controls",
          "Controls",
          `<p class="controller-status"><span class="controller-dot"></span>${this.gamepadIndex === null ? "No controller connected" : "Controller connected"}</p><dl class="controls-guide"><dt>Move between squares</dt><dd>Arrow keys / WASD · Controller D-pad</dd><dt>Select and move a piece</dt><dd>Enter · Controller A</dd><dt>Cycle through pieces</dt><dd>Q / E · Controller LB / RB</dd><dt>Hint</dt><dd>H · Controller X</dd><dt>Undo</dt><dd>U · Controller Y</dd></dl>`,
        ),
      history: () =>
        this.utilityDialog(
          "history",
          "Move trail",
          `<ol class="move-list">${historyRows(view.history)}</ol>`,
        ),
    };
    const generic =
      utilityDialogs[this.modal]?.() ??
      (this.modal === "settings"
        ? this.settingsDialog()
        : this.modal === "import"
          ? this.importDialog()
          : this.modal === "about"
            ? this.aboutDialog()
            : this.modal === "resign"
              ? confirmDialog(
                  "resign-dialog",
                  "Resign this game?",
                  "The current position will be recorded as a resignation.",
                  "confirm-resign",
                  "Resign",
                )
              : "");
    return `${promotion}${gameOver}${generic}`;
  }

  utilityDialog(name, title, content) {
    return `<dialog id="${name}-dialog" aria-labelledby="${name}-title"><div class="dialog-head"><h2 id="${name}-title">${title}</h2><button class="icon-button" data-action="close-dialog" aria-label="Close">×</button></div>${content}</dialog>`;
  }

  settingsDialog() {
    const threeDimensionalSets = this.pieceSets.filter(
      (item) => item.dimension === "3d",
    );
    const pieceSetSetting =
      threeDimensionalSets.length > 1
        ? `<label class="setting-select"><span>3D pieces</span><select data-setting="threePieceSetId">${threeDimensionalSets.map((item) => `<option value="${item.id}" ${selected(this.settings.threePieceSetId, item.id)}>${escapeHtml(item.name)}</option>`).join("")}</select></label>`
        : "";
    return `<dialog id="settings-dialog" aria-labelledby="settings-title"><div class="dialog-head"><div><p class="eyebrow">Make the lodge yours</p><h2 id="settings-title">Settings</h2></div><button class="icon-button" data-action="close-dialog" aria-label="Close">×</button></div><div class="settings-list">
      <label class="setting-select"><span>Board view</span><select data-setting="boardViewMode"><option value="2d" ${selected(this.settings.boardViewMode, "2d")}>2D board</option><option value="3d" ${selected(this.settings.boardViewMode, "3d")}>3D board</option></select></label>${pieceSetSetting}${toggle("coordinates", "Board coordinates", this.settings.coordinates)}${toggle("legalHighlights", "Legal move highlights", this.settings.legalHighlights)}${toggle("reducedMotion", "Reduced motion", this.settings.reducedMotion)}${toggle("resignConfirm", "Confirm before resigning", this.settings.resignConfirm)}${toggle("music", "Music", this.settings.music)}${toggle("sfx", "Sound effects", this.settings.sfx)}
      ${range("musicVolume", "Music volume", this.settings.musicVolume)}${range("sfxVolume", "Effects volume", this.settings.sfxVolume)}
    </div><div class="dialog-actions"><button data-action="clear-data" class="danger-ghost">Clear saved data</button><button class="button primary" data-action="close-dialog">Done</button></div></dialog>`;
  }

  importDialog() {
    return `<dialog id="import-dialog" aria-labelledby="import-title"><div class="dialog-head"><div><p class="eyebrow">Continue another trail</p><h2 id="import-title">Import PGN</h2></div><button class="icon-button" data-action="close-dialog" aria-label="Close">×</button></div><label>Paste a PGN game<textarea id="pgn-input" maxlength="100000" rows="10" placeholder="1. e4 e5 2. Nf3 Nc6 …"></textarea></label><p id="import-error" class="form-error" aria-live="polite"></p><div class="dialog-actions"><button class="button primary" data-action="confirm-import">Import game</button></div></dialog>`;
  }

  aboutDialog() {
    return `<dialog id="about-dialog" aria-labelledby="about-title"><div class="dialog-head"><div><p class="eyebrow">About the game</p><h2 id="about-title">BrawnyAi Chess</h2></div><button class="icon-button" data-action="close-dialog" aria-label="Close">×</button></div><p>Classic chess in a warm Lumberlandia lodge. The complete game runs locally and offline, with no accounts, analytics, ads, or network services.</p><h3>Rules and engine</h3><p>Chess legality is provided by chess.js. Computer play uses Stockfish.js 18.0.8 lite single-threaded WebAssembly.</p><h3>Licenses</h3><p>Stockfish is free software licensed under GNU GPL version 3. Source and redistribution details are included with this release. chess.js is BSD-2-Clause licensed. Three.js, used by the optional 3D board, is MIT licensed.</p><button class="button primary" data-action="close-dialog">Close</button></dialog>`;
  }

  async onClick(event) {
    const viewMode = event.target.closest("[data-view-mode]")?.dataset.viewMode;
    if (viewMode) {
      await this.changeBoardMode(viewMode);
      return;
    }
    const square = event.target.closest("[data-square]")?.dataset.square;
    if (square && this.suppressSquareClick) {
      this.suppressSquareClick = false;
      return;
    }
    if (square) {
      await this.chooseSquare(square);
      return;
    }
    const action = event.target.closest("[data-action]")?.dataset.action;
    const promote = event.target.closest("[data-promote]")?.dataset.promote;
    if (promote) {
      await this.completePromotion(promote);
      return;
    }
    if (!action) {
      return;
    }
    const actions = {
      start: () => this.startGame(),
      resume: () => this.resumeGame(),
      home: () => this.goHome(),
      settings: () => this.showModal("settings"),
      more: () => this.showModal("more"),
      controls: () => this.showModal("controls"),
      history: () => this.showModal("history"),
      about: () => this.showModal("about"),
      import: () => this.showModal("import"),
      "close-dialog": () => this.closeModal(),
      "confirm-import": () => this.importPgn(),
      undo: () => this.undo(),
      hint: () => this.showHint(),
      flip: () => this.flip(),
      "copy-fen": () => this.copyFen(),
      export: () => this.exportPgn(),
      resign: () => this.resign(),
      "confirm-resign": () => this.finishByResignation(),
      rematch: () => this.startGame({ preserve: true }),
      "new-game": () => this.goHome(),
      "clear-data": () => this.clearData(),
      reload: () => location.reload(),
    };
    await actions[action]?.();
  }

  onChange(event) {
    const key = event.target.dataset.setting;
    if (!key) {
      return;
    }
    let value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.type === "range"
          ? Number(event.target.value)
          : event.target.value;
    if (key === "boardViewMode") {
      void this.changeBoardMode(value);
      return;
    }
    if (
      [
        "coordinates",
        "legalHighlights",
        "reducedMotion",
        "resignConfirm",
        "music",
        "sfx",
        "voice",
        "musicVolume",
        "sfxVolume",
        "voiceVolume",
        "threePieceSetId",
      ].includes(key)
    ) {
      this.settings[key] = value;
      this.store.saveSettings(this.settings);
      this.audio?.configure(this.settings);
    } else if (key === "side") {
      this.config.humanSide = value;
    } else {
      this.config[key] = value;
    }
    if (
      this.machine.value === AppState.HOME ||
      key === "coordinates" ||
      key === "legalHighlights" ||
      key === "reducedMotion" ||
      key === "threePieceSetId"
    ) {
      this.render();
    }
  }

  async startGame(options = {}) {
    this.clearIdleTimer();
    this.scrollToTopNext = true;
    this.modal = null;
    this.forcedResult = null;
    this.machine.transition(AppState.INITIALIZING);
    this.engine.stop();
    this.engine.newGame();
    this.positionAnalysis = null;
    this.hint = null;
    this.hintLoading = false;
    this.lastMoveWasEngine = false;
    this.lastHumanMoveContext = null;
    this.lastPhase = "opening";
    this.commentaryProvider.reset();
    this.selected = null;
    this.legal = [];
    let sideChoice = this.config.humanSide;
    let orientationChoice = this.config.orientation;
    if (!options.preserve) {
      const sideSelect =
        this.root.querySelector('[data-setting="side"]')?.value ??
        this.config.humanSide;
      sideChoice = sideSelect;
      const orientationSelect =
        this.root.querySelector('[data-setting="orientation"]')?.value ??
        "auto";
      orientationChoice = orientationSelect;
    }
    const seed = crypto.getRandomValues(new Uint32Array(1))[0];
    this.match = MatchController.create({
      data: this.domainData(),
      engine: this.engine,
      seed,
      mode: this.config.mode,
      opponentId: this.config.opponentId,
      difficultyId: this.config.difficultyId,
      playerSide: sideChoice,
      ...(sideChoice === "random" ? {} : { perspectiveSide: sideChoice }),
    });
    this.session = this.match.session;
    this.config.humanSide = this.match.config.playerSide;
    this.config.orientation =
      orientationChoice === "auto"
        ? this.config.mode === "computer"
          ? this.config.humanSide
          : "w"
        : orientationChoice;
    this.commentaryProvider.random = () =>
      this.match.random.presentation.next();
    const openingEvent = options.preserve
      ? "session_rematch"
      : this.commentaryProvider.sessionEvent();
    this.commentary =
      this.performanceLine(openingEvent, {}, { force: true }) ??
      this.currentOpponent().intro;
    if (this.config.mode === "computer") {
      try {
        await this.engine.initialize();
      } catch (error) {
        this.engineFailure(error.message);
        return;
      }
    }
    await this.afterPositionChange();
  }

  async resumeGame() {
    if (!this.saved) {
      return;
    }
    this.machine.transition(AppState.INITIALIZING);
    this.scrollToTopNext = true;
    try {
      this.session.loadPgn(this.saved.pgn);
      this.config = { ...this.config, ...this.saved.config };
      this.config.humanSide =
        this.saved.config?.playerSide ?? this.saved.config?.humanSide ?? "w";
      this.match = new MatchController({
        data: this.domainData(),
        engine: this.engine,
        session: this.session,
        seed: this.saved.seed ?? 0,
        randomStates: this.saved.randomStates,
        revision: this.saved.revision,
        receiptSequence: this.saved.receiptSequence,
        forcedResult:
          this.saved.forcedResult ??
          (this.saved.reason === "resignation"
            ? { reason: "resignation", result: this.saved.result }
            : null),
        config: {
          ...this.saved.config,
          playerSide: this.config.humanSide,
        },
      });
      this.session = this.match.session;
      this.forcedResult = this.match.forcedResult
        ? {
            ...this.match.forcedResult,
            text: `${this.match.forcedResult.result === "1-0" ? "White" : "Black"} wins by resignation`,
          }
        : null;
      this.commentaryProvider.random = () =>
        this.match.random.presentation.next();
      if (this.session.fen() !== this.saved.fen) {
        console.warn("Save FEN differed from PGN reconstruction; using PGN.");
      }
      if (this.config.mode === "computer") {
        await this.engine.initialize();
      }
      this.commentaryProvider.reset();
      this.hint = null;
      this.hintLoading = false;
      this.lastMoveWasEngine = false;
      this.lastPhase = phaseFor(this.session.view());
      this.commentary =
        this.performanceLine("session_resume", {}, { force: true }) ??
        "The position is just as you left it.";
      await this.afterPositionChange();
    } catch (error) {
      this.machine.transition(AppState.ERROR);
      this.liveMessage = error.message;
      this.render();
    }
  }

  async chooseSquare(square) {
    if (this.machine.value !== AppState.HUMAN_TURN || this.boardAnimating) {
      return;
    }
    const view = this.session.view();
    const piece = view.board.find((item) => item?.square === square);
    if (!this.selected) {
      if (
        piece?.color === view.turn &&
        (this.config.mode === "local" || piece.color === this.config.humanSide)
      ) {
        this.selectSquare(square);
      }
      return;
    }
    const legal = this.legal.find((move) => move.to === square);
    if (legal) {
      const promotionMoves = this.legal.filter(
        (move) => move.to === square && move.promotion,
      );
      if (promotionMoves.length) {
        this.pendingPromotion = { from: this.selected, to: square };
        this.machine.transition(AppState.PROMOTION);
        this.modal = "promotion";
        this.render();
        this.openDialog("promotion");
        return;
      }
      await this.makeMove({ from: this.selected, to: square });
      return;
    }
    if (
      piece?.color === view.turn &&
      (this.config.mode === "local" || piece.color === this.config.humanSide)
    ) {
      this.selectSquare(square);
    } else {
      this.selected = null;
      this.legal = [];
      this.render();
    }
  }

  selectSquare(square) {
    this.selected = square;
    this.focusedSquare = square;
    this.legal = this.session.legalMoves(square);
    this.render();
  }

  async moveFromBoard(move) {
    if (this.boardAnimating || this.machine.value !== AppState.HUMAN_TURN) {
      return;
    }
    this.selected = move.from;
    this.focusedSquare = move.from;
    this.legal = this.session.legalMoves(move.from);
    await this.chooseSquare(move.to);
  }

  async completePromotion(piece) {
    if (!this.pendingPromotion) {
      return;
    }
    const move = { ...this.pendingPromotion, promotion: piece };
    this.pendingPromotion = null;
    this.modal = null;
    await this.makeMove(move);
  }

  async makeMove(move, fromEngine = false) {
    try {
      const beforeFen = this.session.fen();
      const beforeAnalysis =
        !fromEngine && this.positionAnalysis?.fen === beforeFen
          ? this.positionAnalysis
          : null;
      if (!fromEngine) {
        this.engine.stop();
      }
      this.positionAnalysis = null;
      this.hint = null;
      this.hintLoading = false;
      let result;
      if (fromEngine) {
        result = {
          move: this.session.view().history.at(-1),
          view: this.session.view(),
        };
      } else {
        const planned = await this.match.plan({ type: "move", ...move });
        if (!planned.ok) {
          throw new Error(planned.error.message);
        }
        const accepted = await this.match.act({
          ...planned.plan.action,
          planHash: planned.plan.planHash,
        });
        if (!accepted.ok) {
          throw new Error(accepted.error.message);
        }
        result = {
          move: this.session.view().history.at(-1),
          view: this.session.view(),
        };
      }
      this.boardAnimating = true;
      try {
        await this.boardHost.animateMove(result.move, {
          actor: fromEngine ? "opponent" : "player",
        });
      } finally {
        this.boardAnimating = false;
      }
      this.lastMoveWasEngine = fromEngine;
      this.selected = null;
      this.legal = [];
      this.focusedSquare = result.move.to;
      this.audio.playEvent(
        result.move.captured
          ? "capture"
          : result.move.promotion
            ? "promotion"
            : result.move.flags.includes("k") || result.move.flags.includes("q")
              ? "castle"
              : "move",
      );
      const actor = fromEngine ? "jock" : "player";
      const moveEvent = result.view.status.check
        ? `${actor}_check`
        : result.move.captured
          ? `${actor}_capture`
          : result.move.promotion
            ? `${actor}_promotion`
            : result.move.flags.includes("k") || result.move.flags.includes("q")
              ? `${actor}_castle`
              : null;
      if (!fromEngine) {
        this.lastHumanMoveContext = {
          beforeFen,
          beforeAnalysis,
          move: result.move,
        };
      }
      const moveLine = moveEvent
        ? this.performanceLine(moveEvent, {
            ply: result.view.history.length,
            phase: phaseFor(result.view),
          })
        : null;
      if (moveLine) {
        this.commentary = moveLine;
      }
      await this.afterPositionChange(fromEngine);
    } catch (error) {
      this.liveMessage = `Illegal move: ${error.message}`;
      this.render();
    }
  }

  async afterPositionChange(_fromEngine = false) {
    const view = this.session.view();
    const authoritativeStatus = this.match?.status() ?? view.status;
    if (authoritativeStatus.gameOver) {
      this.clearIdleTimer();
      this.machine.transition(AppState.GAME_OVER);
      const event =
        authoritativeStatus.reason === "stalemate"
          ? "outcome_stalemate"
          : authoritativeStatus.result === "1/2-1/2"
            ? "outcome_draw"
            : this.didHumanWin(authoritativeStatus.result)
              ? "outcome_player_win"
              : "outcome_player_loss";
      this.commentary =
        this.performanceLine(event, {}, { force: true }) ??
        this.currentOpponent()[
          event === "outcome_draw"
            ? "draw"
            : event === "outcome_player_win"
              ? "win"
              : "loss"
        ];
      this.audio.playEvent("game_end");
      await this.save();
      this.modal = "gameover";
      this.render();
      this.openDialog("gameover");
      return;
    }
    const engineTurn = this.match
      ? this.match.phase === "opponent_pending"
      : this.config.mode === "computer" && view.turn !== this.config.humanSide;
    const phase = phaseFor(view);
    if (phase !== this.lastPhase) {
      this.lastPhase = phase;
      const phaseLine =
        phase === "middlegame" || phase === "endgame"
          ? this.performanceLine(`phase_${phase}`, { phase })
          : null;
      if (phaseLine) {
        this.commentary = phaseLine;
      }
    }
    this.machine.transition(
      engineTurn ? AppState.ENGINE_THINKING : AppState.HUMAN_TURN,
    );
    await this.save();
    this.liveMessage = this.statusText(view);
    this.render();
    this.audio.playMusic(this.currentOpponent());
    if (engineTurn) {
      await this.requestEngineMove();
    } else {
      const ambient = this.performanceLine("ambient");
      if (ambient) {
        this.commentary = ambient;
        this.render();
      }
      this.scheduleIdleCommentary();
      void this.primePositionAnalysis(view);
    }
  }

  async requestEngineMove() {
    const minimum = Math.max(
      this.currentOpponent().minThinkMs,
      this.currentDifficulty().minThinkMs,
    );
    const started = performance.now();
    try {
      const advanced = await this.match.advanceOne({
        beforeCommit: async ({ analysis }) => {
          if (analysis) {
            this.reactToHumanMove(analysis);
          }
          const elapsed = performance.now() - started;
          if (elapsed < minimum) {
            await new Promise((resolve) =>
              setTimeout(resolve, minimum - elapsed),
            );
          }
          if (this.machine.value !== AppState.ENGINE_THINKING) {
            return false;
          }
          return true;
        },
      });
      if (!advanced.ok) {
        if (advanced.error.code === "stale_plan") {
          return;
        }
        throw new Error(advanced.error.message);
      }
      await this.makeMove(advanced.receipt.action, true);
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      this.engineFailure(error.message);
    }
  }

  async animateComputerMove(move) {
    await this.boardHost.animateMove(move, { actor: "opponent" });
  }

  async primePositionAnalysis(view) {
    if (
      this.config.mode !== "computer" ||
      this.machine.value !== AppState.HUMAN_TURN ||
      view.turn !== this.config.humanSide
    ) {
      return;
    }
    const fen = view.fen;
    this.root.dataset.performanceAnalysis = "pending";
    try {
      const result = await this.engine.analyze(fen, PERFORMANCE_ANALYSIS);
      if (
        this.machine.value === AppState.HUMAN_TURN &&
        this.session.fen() === fen
      ) {
        this.positionAnalysis = {
          fen,
          result,
          humanEvaluation: normalizeEngineScore(
            result.info?.score,
            view.turn,
            this.config.humanSide,
          ),
        };
        this.root.dataset.performanceAnalysis = "ready";
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn("Position analysis failed", error);
      }
      if (this.session.fen() === fen) {
        this.root.dataset.performanceAnalysis = "unavailable";
      }
    }
  }

  reactToHumanMove(analysis) {
    const before = this.lastHumanMoveContext?.beforeAnalysis;
    const view = this.session.view();
    const after = normalizeEngineScore(
      analysis.info?.score,
      view.turn,
      this.config.humanSide,
    );
    const quality = classifyMoveQuality(before?.humanEvaluation, after);
    if (!quality) {
      return;
    }
    const motif = tacticalMotif(this.session, analysis);
    const line = this.performanceLine(
      quality.event,
      {
        humanEvaluation: after,
        mood: moodForHumanEvaluation(after),
        motif,
        ply: view.history.length,
        phase: phaseFor(view),
      },
      { priority: quality.event === "quality_blunder" ? 90 : 60 },
    );
    if (line) {
      this.commentary = line;
      this.render();
    }
  }

  async showHint() {
    const view = this.session.view();
    if (
      this.config.mode !== "computer" ||
      this.machine.value !== AppState.HUMAN_TURN ||
      view.turn !== this.config.humanSide ||
      this.hintLoading
    ) {
      return;
    }

    const fen = view.fen;
    if (this.hint?.fen === fen) {
      this.hint.stage = Math.min(3, this.hint.stage + 1);
      this.hint.message = hintMessage(this.hint.stage, this.hint.move);
      this.commentary = this.hint.message;
      this.liveMessage = this.hint.message;
      this.render();
      return;
    }

    this.clearIdleTimer();
    this.hintLoading = true;
    this.liveMessage = "The Jock is studying the position.";
    this.render();
    try {
      const analysis =
        this.positionAnalysis?.fen === fen
          ? this.positionAnalysis.result
          : await this.engine.analyze(fen, PERFORMANCE_ANALYSIS);
      if (
        this.machine.value !== AppState.HUMAN_TURN ||
        this.session.fen() !== fen
      ) {
        return;
      }
      const proposed = uciToMove(analysis.move);
      const move = proposed
        ? this.session
            .legalMoves(proposed.from)
            .find(
              (legal) =>
                legal.to === proposed.to &&
                (!proposed.promotion || legal.promotion === proposed.promotion),
            )
        : null;
      if (!move) {
        throw new Error("No legal suggestion was available.");
      }
      this.positionAnalysis = {
        fen,
        result: analysis,
        humanEvaluation: normalizeEngineScore(
          analysis.info?.score,
          view.turn,
          this.config.humanSide,
        ),
      };
      this.hint = { fen, stage: 1, move, message: hintMessage(1, move) };
      this.commentary = this.hint.message;
      this.liveMessage = this.hint.message;
      this.root.dataset.performanceAnalysis = "ready";
    } catch (error) {
      if (error.name !== "AbortError") {
        this.liveMessage = "The hint could not be prepared. Try again.";
      }
    } finally {
      this.hintLoading = false;
      if (this.session.fen() === fen) {
        this.render();
      }
    }
  }

  async undo() {
    if (!this.session.view().history.length) {
      return;
    }
    this.engine.stop();
    const planned = await this.match.plan({ type: "undo" });
    if (!planned.ok) {
      return;
    }
    const accepted = await this.match.act({
      type: "undo",
      planHash: planned.plan.planHash,
    });
    if (!accepted.ok) {
      return;
    }
    this.positionAnalysis = null;
    this.hint = null;
    this.hintLoading = false;
    this.lastMoveWasEngine = false;
    this.commentary =
      this.performanceLine("undo", {}, { force: true }) ??
      "Let's take that move back.";
    this.selected = null;
    this.legal = [];
    if (this.machine.value !== AppState.HUMAN_TURN) {
      this.machine.transition(AppState.HUMAN_TURN);
    }
    await this.save();
    this.render();
  }

  flip() {
    this.config.orientation = this.config.orientation === "w" ? "b" : "w";
    this.save();
    this.render();
  }
  copyFen() {
    navigator.clipboard.writeText(this.session.fen());
    this.liveMessage = "FEN copied to clipboard.";
    this.render();
  }

  exportPgn() {
    const blob = new Blob([this.session.pgn()], {
      type: "application/x-chess-pgn",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `brawnyai-chess-${new Date().toISOString().slice(0, 10)}.pgn`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async importPgn() {
    const input = this.root.querySelector("#pgn-input");
    try {
      this.engine.stop();
      this.session.loadPgn(input.value);
      this.config.mode = "local";
      this.config.orientation = "w";
      this.config.humanSide = "w";
      this.match = new MatchController({
        data: this.domainData(),
        engine: this.engine,
        session: this.session,
        seed: crypto.getRandomValues(new Uint32Array(1))[0],
        config: {
          mode: "local",
          opponentId: this.config.opponentId,
          difficultyId: this.config.difficultyId,
          playerSide: "w",
          perspectiveSide: "w",
        },
      });
      this.lastMoveWasEngine = false;
      this.modal = null;
      if (this.machine.value === AppState.HOME) {
        this.machine.transition(AppState.INITIALIZING);
      }
      await this.afterPositionChange();
    } catch (error) {
      this.root.querySelector("#import-error").textContent =
        `Could not import: ${error.message}`;
    }
  }

  resign() {
    if (this.settings.resignConfirm) {
      this.showModal("resign");
    } else {
      this.finishByResignation();
    }
  }
  async finishByResignation() {
    this.engine.stop();
    this.modal = null;
    const planned = await this.match.plan({ type: "resign" });
    if (!planned.ok) {
      return;
    }
    const accepted = await this.match.act({
      type: "resign",
      planHash: planned.plan.planHash,
    });
    if (!accepted.ok) {
      return;
    }
    const winner = accepted.status.result === "1-0" ? "White" : "Black";
    this.forcedResult = {
      text: `${winner} wins by resignation`,
      result: accepted.status.result,
    };
    this.machine.transition(AppState.GAME_OVER);
    this.commentary =
      this.performanceLine("outcome_resignation", {}, { force: true }) ??
      "The game ends by resignation. The board will be ready when you are.";
    await this.save({
      result: this.forcedResult.result,
      reason: "resignation",
    });
    this.render();
    this.openDialog("gameover");
  }
  async goHome() {
    this.clearIdleTimer();
    this.engine.stop();
    this.pendingPromotion = null;
    this.modal = null;
    if (this.machine.value !== AppState.HOME) {
      this.machine.transition(AppState.HOME);
    }
    this.saved = await this.store.loadActive();
    this.render();
  }
  showModal(name) {
    if (!this.modal) {
      this.modalReturnAction =
        document.activeElement?.getAttribute("data-action") ?? name;
    }
    this.modal = name;
    this.render();
    this.openDialog(name);
  }
  closeModal() {
    this.root.querySelector("dialog[open]")?.close();
    this.modal = null;
    if (this.machine.value === AppState.PROMOTION) {
      this.pendingPromotion = null;
      this.machine.transition(AppState.HUMAN_TURN);
    }
    this.render();
    const trigger = [...this.root.querySelectorAll("[data-action]")].find(
      (element) =>
        element.getAttribute("data-action") === this.modalReturnAction &&
        !element.closest("dialog"),
    );
    trigger?.focus({ preventScroll: true });
  }
  openDialog(name) {
    const dialog = this.root.querySelector(`#${name}-dialog`);
    if (dialog && !dialog.open) {
      dialog.addEventListener(
        "cancel",
        (event) => {
          event.preventDefault();
          this.closeModal();
        },
        { once: true },
      );
      dialog.showModal();
    }
  }
  async clearData() {
    await this.store.clear();
    this.saved = null;
    this.settings = { ...DEFAULT_SETTINGS };
    this.modal = null;
    await this.goHome();
  }

  async save(overrides = {}) {
    const view = this.session.view();
    const matchState = this.match?.state();
    await this.store.saveActive({
      pgn: view.pgn,
      fen: view.fen,
      config: {
        ...this.config,
        playerSide: this.config.humanSide,
        perspectiveSide:
          matchState?.config.perspectiveSide ?? this.config.humanSide,
      },
      seed: matchState?.seed,
      randomStates: matchState?.randomStates,
      revision: matchState?.revision,
      receiptSequence: matchState?.receiptSequence,
      forcedResult: matchState?.forcedResult,
      result: view.status.result,
      reason: view.status.reason,
      ...overrides,
    });
    this.saved = await this.store.loadActive();
  }

  engineFailure(message) {
    this.clearIdleTimer();
    this.engine.stop();
    this.config.mode = "local";
    if (this.match) {
      this.match.config.mode = "local";
      this.match.phase = "decision_required";
    }
    if (this.machine.value !== AppState.ERROR) {
      this.machine.transition(AppState.ERROR);
    }
    this.liveMessage = message;
    this.commentary =
      this.performanceLine("engine_error", {}, { force: true }) ??
      "Computer play is unavailable. Local play remains ready.";
    this.machine.transition(AppState.HUMAN_TURN);
    this.render();
  }

  performanceLine(event, context = {}, options = {}) {
    if (!this.commentaryProvider) {
      return null;
    }
    const view = this.session.view();
    return this.commentaryProvider.line(
      event,
      this.config.mode === "local" ? "__fallback__" : this.currentOpponent().id,
      {
        ply: view.history.length,
        phase: phaseFor(view),
        period: this.commentaryProvider.dayPeriod(),
        mood: context.mood ?? moodForHumanEvaluation(context.humanEvaluation),
        ...context,
      },
      options,
    );
  }

  scheduleIdleCommentary() {
    this.clearIdleTimer();
    if (this.config.mode !== "computer") {
      return;
    }
    const fen = this.session.fen();
    this.idleTimer = setTimeout(() => {
      if (
        this.machine.value !== AppState.HUMAN_TURN ||
        this.session.fen() !== fen
      ) {
        return;
      }
      const line = this.performanceLine("idle_thinking");
      if (line) {
        this.commentary = line;
        this.render();
      }
    }, 45_000);
  }

  clearIdleTimer() {
    clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  onKeyDown(event) {
    const dialog = event.target.closest("dialog[open]");
    if (dialog && event.key === "Tab") {
      const controls = [
        ...dialog.querySelectorAll(
          "button, input, select, textarea, [tabindex]",
        ),
      ].filter(
        (element) =>
          !element.disabled &&
          element.tabIndex >= 0 &&
          element.getClientRects().length,
      );
      const index = controls.indexOf(document.activeElement);
      const next =
        index < 0
          ? event.shiftKey
            ? controls.length - 1
            : 0
          : (index + (event.shiftKey ? -1 : 1) + controls.length) %
            controls.length;
      event.preventDefault();
      controls[next]?.focus();
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const square = event.target.closest("[data-square]")?.dataset.square;
    if (!square) {
      if (event.key === "Escape" && this.modal) {
        event.preventDefault();
        this.closeModal();
      }
      return;
    }
    const key = event.key.toLowerCase();
    const directions = {
      arrowleft: "left",
      a: "left",
      arrowright: "right",
      d: "right",
      arrowup: "up",
      w: "up",
      arrowdown: "down",
      s: "down",
    };
    if (directions[key]) {
      event.preventDefault();
      this.moveBoardFocus(directions[key]);
    } else if (event.key === "Home") {
      event.preventDefault();
      const order = orderedSquares(this.config.orientation);
      const index = order.indexOf(square);
      this.focusedSquare = order[Math.floor(index / 8) * 8];
      this.render();
    } else if (event.key === "End") {
      event.preventDefault();
      const order = orderedSquares(this.config.orientation);
      const index = order.indexOf(square);
      this.focusedSquare = order[Math.floor(index / 8) * 8 + 7];
      this.render();
    } else if (["Enter", " "].includes(event.key)) {
      event.preventDefault();
      this.chooseSquare(square);
    } else if (key === "q" || key === "e") {
      event.preventDefault();
      this.cycleBoardFocus(key === "q" ? -1 : 1);
    } else if (key === "h") {
      event.preventDefault();
      this.showHint();
    } else if (key === "u") {
      event.preventDefault();
      this.undo();
    } else if (key === "f") {
      event.preventDefault();
      this.flip();
    } else if (event.key === "Escape") {
      this.cancelBoardSelection();
    }
  }

  moveBoardFocus(direction) {
    this.focusedSquare = moveBoardSquare(
      this.focusedSquare,
      direction,
      this.config.orientation,
    );
    this.render();
  }

  cycleBoardFocus(step) {
    const view = this.session.view();
    const candidates = this.selected
      ? [...new Set(this.legal.map((move) => move.to))]
      : view.board
          .filter(
            (piece) =>
              piece?.color === view.turn &&
              (this.config.mode === "local" ||
                piece.color === this.config.humanSide),
          )
          .map((piece) => piece.square);
    this.focusedSquare = cycleBoardSquare(
      this.focusedSquare,
      candidates,
      step,
      this.config.orientation,
    );
    const piece = view.board.find(
      (candidate) => candidate?.square === this.focusedSquare,
    );
    this.liveMessage = this.selected
      ? `Legal destination ${this.focusedSquare}`
      : piece
        ? `${piece.color === "w" ? "White" : "Black"} ${PIECE_NAMES[piece.type]} at ${this.focusedSquare}`
        : `Square ${this.focusedSquare}`;
    this.render();
  }

  cancelBoardSelection() {
    this.selected = null;
    this.legal = [];
    this.liveMessage = "Piece selection cancelled.";
    this.render();
  }

  connectGamepad(gamepad) {
    if (!gamepad || this.gamepadIndex === gamepad.index) {
      return;
    }
    this.gamepadIndex = gamepad.index;
    this.gamepadButtons = [];
    this.gamepadHeldDirection = null;
    this.liveMessage = `${gamepad.id || "Controller"} connected. Use the D-pad or left stick to move.`;
    this.render();
    if (this.gamepadFrame === null) {
      this.gamepadFrame = requestAnimationFrame((time) =>
        this.pollGamepad(time),
      );
    }
  }

  disconnectGamepad(gamepad) {
    if (gamepad?.index !== this.gamepadIndex) {
      return;
    }
    const replacement = [...(navigator.getGamepads?.() ?? [])].find(
      (candidate) => candidate && candidate.index !== gamepad.index,
    );
    if (replacement) {
      this.gamepadIndex = null;
      this.connectGamepad(replacement);
      return;
    }
    this.gamepadIndex = null;
    this.gamepadButtons = [];
    this.gamepadHeldDirection = null;
    if (this.gamepadFrame !== null) {
      cancelAnimationFrame(this.gamepadFrame);
      this.gamepadFrame = null;
    }
    this.liveMessage =
      "Controller disconnected. Keyboard controls remain ready.";
    this.render();
  }

  pollGamepad(time) {
    this.gamepadFrame = null;
    const gamepad = navigator.getGamepads?.()[this.gamepadIndex];
    if (!gamepad) {
      this.gamepadIndex = null;
      this.gamepadButtons = [];
      this.gamepadHeldDirection = null;
      this.render();
      return;
    }

    const direction = gamepadDirection(gamepad);
    if (direction !== this.gamepadHeldDirection) {
      this.gamepadHeldDirection = direction;
      this.gamepadRepeatAt = time + 360;
      if (direction) {
        this.handleGamepadDirection(direction);
      }
    } else if (direction && time >= this.gamepadRepeatAt) {
      this.gamepadRepeatAt = time + 115;
      this.handleGamepadDirection(direction);
    }

    const pressed = gamepad.buttons.map((button) => button.pressed);
    for (const index of [0, 1, 2, 3, 4, 5, 9]) {
      if (pressed[index] && !this.gamepadButtons[index]) {
        this.handleGamepadButton(index);
      }
    }
    this.gamepadButtons = pressed;
    this.gamepadFrame = requestAnimationFrame((nextTime) =>
      this.pollGamepad(nextTime),
    );
  }

  handleGamepadDirection(direction) {
    const dialog = this.root.querySelector("dialog[open]");
    if (dialog) {
      this.moveControllerFocus(dialog, direction);
      return;
    }
    if (this.machine.value === AppState.HOME) {
      const main = this.root.querySelector("#main");
      const active = document.activeElement;
      if (
        active?.matches("select") &&
        (direction === "left" || direction === "right")
      ) {
        const delta = direction === "left" ? -1 : 1;
        active.selectedIndex = Math.max(
          0,
          Math.min(active.options.length - 1, active.selectedIndex + delta),
        );
        active.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        this.moveControllerFocus(main, direction);
      }
      return;
    }
    this.moveBoardFocus(direction);
  }

  moveControllerFocus(container, direction) {
    const controls = [
      ...container.querySelectorAll(
        "button:not(:disabled), select:not(:disabled), input:not(:disabled), textarea:not(:disabled)",
      ),
    ];
    if (!controls.length) {
      return;
    }
    const current = controls.indexOf(document.activeElement);
    const step = direction === "left" || direction === "up" ? -1 : 1;
    const next =
      current < 0
        ? step < 0
          ? controls.length - 1
          : 0
        : (current + step + controls.length) % controls.length;
    controls[next].focus();
  }

  handleGamepadButton(index) {
    const dialog = this.root.querySelector("dialog[open]");
    if (dialog) {
      if (index === 0) {
        const active = document.activeElement;
        const button = dialog.contains(active)
          ? active
          : dialog.querySelector("button:not(:disabled)");
        button?.click();
      } else if (index === 1 && this.modal !== "gameover") {
        this.closeModal();
      }
      return;
    }
    if (this.machine.value === AppState.HOME) {
      if (index === 0) {
        const active = document.activeElement;
        if (active?.matches("button")) {
          active.click();
        }
      } else if (index === 9) {
        this.root.querySelector('[data-action="start"]')?.click();
      }
      return;
    }
    if (index === 0) {
      this.chooseSquare(this.focusedSquare);
    }
    if (index === 1) {
      this.cancelBoardSelection();
    }
    if (index === 2) {
      this.showHint();
    }
    if (index === 3) {
      this.undo();
    }
    if (index === 4) {
      this.cycleBoardFocus(-1);
    }
    if (index === 5) {
      this.cycleBoardFocus(1);
    }
    if (index === 9) {
      this.showModal("settings");
    }
  }

  onPointerDown(event) {
    const squareElement = event.target.closest("[data-square]");
    const square = squareElement?.dataset.square;
    if (!square || this.machine.value !== AppState.HUMAN_TURN) {
      return;
    }
    const piece = this.session
      .view()
      .board.find((item) => item?.square === square);
    if (
      piece?.color === this.session.turn() &&
      (this.config.mode === "local" || piece.color === this.config.humanSide)
    ) {
      this.dragFrom = square;
      this.dragPointerId = event.pointerId;
      this.dragStart = { x: event.clientX, y: event.clientY };
      this.dragMoved = false;
      try {
        squareElement.setPointerCapture?.(event.pointerId);
      } catch {
        // Synthetic accessibility tooling may not register an active pointer.
      }
    }
  }

  onPointerMove(event) {
    if (
      !this.dragFrom ||
      event.pointerId !== this.dragPointerId ||
      !this.dragStart
    ) {
      return;
    }
    const distance = Math.hypot(
      event.clientX - this.dragStart.x,
      event.clientY - this.dragStart.y,
    );
    if (!this.dragMoved && distance >= 6) {
      this.dragMoved = true;
      this.startDragVisual(this.dragFrom, event);
    }
    if (this.dragMoved) {
      event.preventDefault();
      this.positionDragGhost(event);
      this.updateDragTarget(event);
    }
  }

  async onPointerUp(event) {
    if (!this.dragFrom || event.pointerId !== this.dragPointerId) {
      return;
    }
    const from = this.dragFrom;
    const moved = this.dragMoved;
    const target = /** @type {HTMLElement | null} */ (
      document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest?.("[data-square]")
    )?.dataset.square;
    this.cancelDrag();
    if (moved) {
      this.suppressSquareClick = true;
      setTimeout(() => {
        this.suppressSquareClick = false;
      });
    }
    if (moved && target && target !== from) {
      this.selected = from;
      this.legal = this.session.legalMoves(from);
      await this.chooseSquare(target);
    }
  }

  startDragVisual(square, event) {
    const source = this.root.querySelector(`[data-square="${square}"]`);
    const piece = source?.querySelector(".piece");
    if (!source || !piece) {
      return;
    }
    source.classList.add("selected", "drag-source");
    for (const move of this.session.legalMoves(square)) {
      const destination = this.root.querySelector(`[data-square="${move.to}"]`);
      destination?.classList.add("legal");
      if (move.captured) {
        destination?.classList.add("capture");
      }
    }
    const bounds = source.getBoundingClientRect();
    const ghost = document.createElement("div");
    ghost.className = "drag-ghost";
    ghost.setAttribute("aria-hidden", "true");
    ghost.style.width = `${bounds.width}px`;
    ghost.style.height = `${bounds.height}px`;
    ghost.append(piece.cloneNode(true));
    document.body.append(ghost);
    this.dragGhost = ghost;
    this.positionDragGhost(event);
  }

  positionDragGhost(event) {
    if (!this.dragGhost) {
      return;
    }
    this.dragGhost.style.left = `${event.clientX}px`;
    this.dragGhost.style.top = `${event.clientY}px`;
  }

  updateDragTarget(event) {
    this.root
      .querySelectorAll(".square.drag-over")
      .forEach((square) => square.classList.remove("drag-over"));
    document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest?.(".square.legal")
      ?.classList.add("drag-over");
  }

  cancelDrag() {
    if (this.dragPointerId !== null) {
      const source = this.dragFrom
        ? this.root.querySelector(`[data-square="${this.dragFrom}"]`)
        : null;
      if (source?.hasPointerCapture?.(this.dragPointerId)) {
        source.releasePointerCapture(this.dragPointerId);
      }
    }
    this.dragGhost?.remove();
    this.dragGhost = null;
    this.dragFrom = null;
    this.dragPointerId = null;
    this.dragStart = null;
    this.dragMoved = false;
    this.root
      .querySelectorAll(
        ".square.drag-source, .square.drag-over, .square.legal, .square.capture",
      )
      .forEach((square) =>
        square.classList.remove(
          "drag-source",
          "drag-over",
          "selected",
          "legal",
          "capture",
        ),
      );
  }

  applyTheme(theme) {
    this.theme = theme;
    for (const [key, value] of Object.entries(theme.tokens)) {
      if (
        /^(#[0-9a-f]{3,8}|-?\d+(\.\d+)?(px|rem|em|%)|0\s.+|none)$/i.test(value)
      ) {
        document.documentElement.style.setProperty(
          `--${camelToKebab(key)}`,
          value,
        );
      }
    }
  }

  statusText(view) {
    if (view.status.gameOver) {
      return view.status.result === "1/2-1/2"
        ? `Draw by ${view.status.reason}`
        : `${view.status.result === "1-0" ? "White" : "Black"} wins by ${view.status.reason}`;
    }
    if (this.machine.value === AppState.ENGINE_THINKING) {
      return `${this.currentOpponent().name} is thinking`;
    }
    return view.status.check
      ? `${view.turn === "w" ? "White" : "Black"} is in check`
      : `${view.turn === "w" ? "White" : "Black"} to move`;
  }

  didHumanWin(result) {
    return (
      (this.config.humanSide === "w" && result === "1-0") ||
      (this.config.humanSide === "b" && result === "0-1")
    );
  }
  currentOpponent() {
    return (
      this.opponents.find((item) => item.id === this.config.opponentId) ??
      this.opponents[0]
    );
  }
  currentDifficulty() {
    return (
      this.difficulties.find((item) => item.id === this.config.difficultyId) ??
      this.difficulties[0]
    );
  }

  domainData() {
    return {
      game: this.game,
      opponents: this.opponents,
      difficulties: this.difficulties,
      openingBooks: this.openingBooks,
    };
  }

  registerServiceWorker() {
    if (
      !("serviceWorker" in navigator) ||
      !navigator.onLine ||
      (location.hostname === "127.0.0.1" && location.port === "4173")
    ) {
      return;
    }
    const replacingActiveRelease = Boolean(navigator.serviceWorker.controller);
    let reloadingForUpdate = false;
    if (replacingActiveRelease) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloadingForUpdate) {
          return;
        }
        reloadingForUpdate = true;
        location.reload();
      });
    }
    navigator.serviceWorker
      .register(`./sw.js?version=${encodeURIComponent(this.releaseId)}`, {
        updateViaCache: "none",
      })
      .then((registration) => {
        registration.addEventListener("updatefound", () => {
          const worker = registration.installing;
          worker?.addEventListener("statechange", () => {
            if (
              worker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              this.liveMessage =
                "An update is ready. It will load next time you open the game.";
              this.render();
            }
          });
        });
      })
      .catch((error) =>
        console.warn("Service worker registration failed", error),
      );
  }
}

async function loadJson(name) {
  const response = await fetch(`./generated/${name}.json`);
  if (!response.ok) {
    throw new Error(`Required game data is unavailable: ${name}`);
  }
  return response.json();
}

function cssAssetUrl(entry) {
  return entry ? new URL(entry.url, document.baseURI).href : null;
}

function squareColor(square) {
  return (square.charCodeAt(0) - 97 + Number(square[1])) % 2 === 1
    ? "dark"
    : "light";
}
function avatar(opponent) {
  return `<div class="opponent-avatar fallback" aria-label="${escapeHtml(opponent.name)}"><span>${opponent.name
    .split(/\s+/)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")}</span></div>`;
}
function selected(value, candidate) {
  return value === candidate ? "selected" : "";
}
function title(value) {
  return value[0].toUpperCase() + value.slice(1);
}
function camelToKebab(value) {
  return value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}
function escapeHtml(value = "") {
  return String(value).replace(
    /[&<>"]/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[character],
  );
}
function toggle(key, label, checked) {
  return `<label class="setting-row"><span>${label}</span><input type="checkbox" data-setting="${key}" ${checked ? "checked" : ""}></label>`;
}
function range(key, label, value) {
  return `<label class="setting-range"><span>${label}</span><input type="range" min="0" max="1" step="0.05" value="${value}" data-setting="${key}"></label>`;
}
function confirmDialog(id, heading, copy, action, label) {
  return `<dialog id="${id}" aria-labelledby="${id}-title"><h2 id="${id}-title">${heading}</h2><p>${copy}</p><div class="dialog-actions"><button data-action="close-dialog">Cancel</button><button class="button danger" data-action="${action}">${label}</button></div></dialog>`;
}

function hintMessage(stage, move) {
  const piece = PIECE_NAMES[move.piece] ?? "piece";
  if (stage === 1) {
    if (move.flags.includes("k") || move.flags.includes("q")) {
      return "Get your king settled before the position gets heavier.";
    }
    if (move.captured) {
      return "Something on his side is available. Check every contact.";
    }
    if (move.piece === "p") {
      return "One pawn step can give your whole position better shape.";
    }
    return `Your ${piece} can take a more useful role here.`;
  }
  if (stage === 2) {
    return `Put your eyes on the ${piece}.`;
  }
  const promotion = move.promotion
    ? ` and promote it to a ${PIECE_NAMES[move.promotion]}`
    : "";
  return `Try the ${piece} from ${move.from} to ${move.to}${promotion}.`;
}

function historyRows(history) {
  if (!history.length) {
    return `<li class="empty-history">The first move is yours.</li>`;
  }
  let rows = "";
  for (let index = 0; index < history.length; index += 2) {
    rows += `<li><span>${index / 2 + 1}.</span><button tabindex="-1">${escapeHtml(history[index]?.san ?? "")}</button><button tabindex="-1">${escapeHtml(history[index + 1]?.san ?? "")}</button></li>`;
  }
  return rows;
}

function capturedPieces(history) {
  const captured = history
    .filter((move) => move.captured)
    .map((move) => PIECES[(move.color === "w" ? "b" : "w") + move.captured]);
  return captured.length
    ? captured.join(" ")
    : `<span class="muted">No captures yet</span>`;
}

new ChessApp(document.querySelector("#app")).initialize();
